import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Card, CardHeader, CardTitle, CardBody, CardFooter } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input, { Label, Select } from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import { getRoleColor } from '../lib/utils';
import { User, Lock, Globe, CheckCircle } from 'lucide-react';

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, updateLanguage, changePassword } = useAuthStore();

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  const handleLanguageChange = async () => {
    setIsChangingLanguage(true);
    const result = await updateLanguage(selectedLanguage);
    if (result.success) {
      i18n.changeLanguage(selectedLanguage);
    }
    setIsChangingLanguage(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('errors.passwordMismatch') || 'Passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError(t('errors.minLength', { min: 6 }));
      return;
    }

    setIsChangingPassword(true);
    const result = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);

    if (result.success) {
      setPasswordSuccess(true);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } else {
      setPasswordError(result.error || 'Failed to change password');
    }
    setIsChangingPassword(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{t('profile.title')}</h1>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {t('nav.profile')}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('auth.username')}</p>
              <p className="font-medium">{user?.username}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('users.fullName')}</p>
              <p className="font-medium">{user?.fullName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('reports.division')}</p>
              <p className="font-medium">{user?.division || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('users.role')}</p>
              <Badge className={getRoleColor(user?.role)}>
                {t(`roles.${user?.role}`)}
              </Badge>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Language Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t('profile.language')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex items-center gap-4">
            <Select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-48"
            >
              <option value="zh-TW">{t('language.zhTW')}</option>
              <option value="en">{t('language.en')}</option>
            </Select>
            <Button
              onClick={handleLanguageChange}
              isLoading={isChangingLanguage}
              disabled={selectedLanguage === i18n.language}
            >
              {t('common.save')}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            {t('profile.changePassword')}
          </CardTitle>
        </CardHeader>
        <form onSubmit={handlePasswordChange}>
          <CardBody className="space-y-4">
            {passwordSuccess && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800">
                <CheckCircle className="w-5 h-5" />
                {t('users.passwordChanged')}
              </div>
            )}

            {passwordError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800">
                {passwordError}
              </div>
            )}

            <div>
              <Label required>{t('users.currentPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
                }
                autoComplete="current-password"
              />
            </div>

            <div>
              <Label required>{t('users.newPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
                }
                autoComplete="new-password"
              />
            </div>

            <div>
              <Label required>{t('users.confirmPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                }
                autoComplete="new-password"
              />
            </div>
          </CardBody>

          <CardFooter>
            <div className="flex justify-end">
              <Button
                type="submit"
                isLoading={isChangingPassword}
                disabled={
                  !passwordForm.currentPassword ||
                  !passwordForm.newPassword ||
                  !passwordForm.confirmPassword
                }
              >
                {t('profile.changePassword')}
              </Button>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
